import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ChatGrid from '@/components/chat/ChatGrid';
import EmptyChatsState from '@/components/chat/EmptyChatsState';
import { Chat } from '@/types';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

export interface ChatTabsProps {
  recentChats: Chat[];
  favoriteChats: Chat[];
  popularChats: Chat[];
  handleNewChat: () => void;
  loadingMessages?: boolean;
  onLoadMore?: () => Promise<void>;
  hasMore?: boolean;
  onOpenChat?: (id: string, title?: string) => void;
}

const ChatTabs = ({
  recentChats,
  favoriteChats,
  popularChats,
  handleNewChat,
  loadingMessages,
  onLoadMore,
  hasMore = false,
  onOpenChat,
}: ChatTabsProps) => {
  const [activeTab, setActiveTab] = useState('recent');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [visibleTabContent, setVisibleTabContent] = useState<Chat[]>(recentChats);

  useEffect(() => {
    switch (activeTab) {
      case 'favorites':
        setVisibleTabContent(favoriteChats);
        break;
      case 'popular':
        setVisibleTabContent(popularChats);
        break;
      default:
        setVisibleTabContent(recentChats);
    }
  }, [activeTab, favoriteChats, popularChats, recentChats]);

  const showEmptyState =
    recentChats.length === 0 &&
    favoriteChats.length === 0 &&
    popularChats.length === 0;

  const handleLoadMore = async () => {
    if (onLoadMore && !isLoadingMore) {
      setIsLoadingMore(true);
      try {
        await onLoadMore();
      } finally {
        setIsLoadingMore(false);
      }
    }
  };

  if (showEmptyState) {
    return (
      <EmptyChatsState
        onNewChat={handleNewChat}
        title="No chats found"
        description="Get started by creating a new chat"
      />
    );
  }

  return (
    <Tabs defaultValue="recent" onValueChange={setActiveTab} className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="recent" className="flex-1 sm:flex-none">
          Recent
        </TabsTrigger>
        <TabsTrigger value="favorites" className="flex-1 sm:flex-none">
          Favorites {favoriteChats.length > 0 && `(${favoriteChats.length})`}
        </TabsTrigger>
        <TabsTrigger value="popular" className="flex-1 sm:flex-none">
          Popular
        </TabsTrigger>
      </TabsList>

      <TabsContent value="recent" className="mt-0">
        {recentChats.length > 0 ? (
          <>
            <ChatGrid chats={recentChats} loadingMessages={loadingMessages} onOpenChat={onOpenChat} />
            {hasMore && onLoadMore && (
              <div className="mt-6 text-center">
                <Button variant="outline" onClick={handleLoadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading more...
                    </>
                  ) : (
                    'Load more chats'
                  )}
                </Button>
              </div>
            )}
          </>
        ) : (
          <p className="mt-8 text-center text-muted-foreground">No recent chats found.</p>
        )}
      </TabsContent>

      <TabsContent value="favorites" className="mt-0">
        {favoriteChats.length > 0 ? (
          <ChatGrid chats={favoriteChats} loadingMessages={loadingMessages} onOpenChat={onOpenChat} />
        ) : (
          <p className="mt-8 text-center text-muted-foreground">
            No favorite chats found. Mark chats as favorites to see them here.
          </p>
        )}
      </TabsContent>

      <TabsContent value="popular" className="mt-0">
        {popularChats.length > 0 ? (
          <ChatGrid chats={popularChats} loadingMessages={loadingMessages} onOpenChat={onOpenChat} />
        ) : (
          <p className="mt-8 text-center text-muted-foreground">No popular chats found.</p>
        )}
      </TabsContent>
    </Tabs>
  );
};

export default React.memo(ChatTabs);
